import styled from '@emotion/styled'
import { set } from 'lodash'
import { useState } from 'react'

import {
  ApiClientDefaultCashInstruments,
  ApiClientDefaultFees,
  ApiClientTradeBuildingSettings,
  ApiClientType,
  ApiFee,
  ApiFeeBasis,
  ApiFeeDeductionFrequency,
  ApiFeeType,
  ApiNewBusinessUnit,
  ApiReinvestmentType
} from '@/api/_legacy/generated-api'
import { AddRemoveButton, Select, Space, TextInput } from '@/components/core-v2'
import { formatFees, unpascal, unpascalFeeType } from '@/modules/tradebuilder-v2/util'
import { createEmptyFee, getGUID } from '@/utilities'

import Icon from '../../atoms/Icon'
import { Mutation, StyledFeesBox } from './primitives'

type Client = {
  businessUnits: ApiNewBusinessUnit[]
  clientName: string
  clientType: ApiClientType
  defaultCashInstruments: ApiClientDefaultCashInstruments
  defaultFees?: ApiClientDefaultFees
  disallowedStrategies?: string[]
  reinvestmentType: ApiReinvestmentType
  tradeBuildingSettings: ApiClientTradeBuildingSettings
}

export type FeesInputProps = {
  availableCptyIds: string[]
  availableFeeBases: string[]
  availableFeeFreqs: string[]
  availableFeeTypes: string[]
  bankGuarantee?: string
  displayFees: ApiFee[]
  feeCategory: string
  isLeveraged?: string
  newFee?: ApiFee
  onAddNewFee?: (newFee: ApiFee) => void
  onChangeClient?: (mutation: Mutation<Client>) => void
  onChangeNewFee?: (mutation: Mutation<ApiFee>) => void
  onFeePropertyChange?: (index: number, property: string, updatedValue: any) => void
  onRemoveExistingFee?: (index: number) => void
  readonly?: boolean
  strategyFeeIndex?: number
  title: string
}

const StyledTable = styled.table`
  width: 100%;
  padding: 4px;
  margin-bottom: 4px;
  border-collapse: unset;
  font-size: 11px;
  * {
    appearance: revert;
  }
  thead {
    tr {
      th {
        padding: 0 8px 16px;
        text-align: left;
      }
      th:first-of-type {
        font-weight: 600;
      }
    }
  }

  tbody {
    tr {
      td {
        margin: 0 8px 0;
        background: var(--White99);
        padding: 4px;
        border-bottom: Solid 4px var(--White);
        select {
          padding: 8px 4px;
          font-size: 11px;
        }
        button {
          min-width: 22px;
          padding: 8px 2px;
          border-radius: 4px;
          &:hover {
            background: var(--Graphite);
            color: var(--White);
          }
        }
        button[disabled] {
          pointer-events: none;
        }
      }
    }
  }
`

const StyledHeading = styled.div`
  width: 100%;
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  &:empty {
    display: none;
    margin: 0;
  }
  span.title {
    font-weight: 600;
    margin: 0 0 16px;
    &:empty {
      display: none;
    }
  }
  span.sub-title {
    font-weight: normal;
    margin: 0 0 16px;
    strong {
      text-transform: uppercase;
      span {
        display: none;
      }
      &.False:after {
        content: 'No';
      }
      &.True:after {
        content: 'Yes';
      }
    }
    &:empty {
      display: none;
    }
  }
`

export function FeesInput({
  availableCptyIds,
  availableFeeBases,
  availableFeeFreqs,
  availableFeeTypes,
  bankGuarantee,
  displayFees,
  feeCategory,
  isLeveraged,
  newFee,
  onAddNewFee,
  onChangeClient,
  onChangeNewFee,
  onFeePropertyChange,
  onRemoveExistingFee,
  readonly,
  strategyFeeIndex,
  title
}: FeesInputProps) {
  const [emptyNewFee, setEmptyNewFee] = useState<ApiFee>(createEmptyFee())

  const onNewFeePropertyUpdate = (propertyName: string, value: any) => {
    const cloned = { ...emptyNewFee }
    set(cloned, propertyName, value)
    setEmptyNewFee(cloned)
  }

  const onAddValidNewFee = () => {
    if (onAddNewFee) {
      onAddNewFee(emptyNewFee)
      setEmptyNewFee(createEmptyFee())
    }
  }

  const allCptyIds = [...new Set(['Absa', ...(availableCptyIds ?? [])])]

  const newFeeAllowed = feeCategory !== 'portfolio' || displayFees.length === 0

  return (
    <StyledFeesBox>
      <StyledHeading>
        <span className="sub-title">
          {bankGuarantee !== undefined && (
            <>
              Bank protection:&nbsp;
              <strong className={bankGuarantee}>
                <span>{bankGuarantee}</span>
              </strong>
              &nbsp;&nbsp;
            </>
          )}
          {isLeveraged !== undefined && (
            <>
              &nbsp;&nbsp;Leveraged:&nbsp;
              <strong className={isLeveraged}>
                <span>{isLeveraged}</span>
              </strong>
            </>
          )}
        </span>
      </StyledHeading>
      <StyledTable>
        <thead>
          <tr>
            <th>{title}</th>
            <th>Calculated on</th>
            <th>Frequency</th>
            <th>VAT</th>
            <th>Counterparty</th>
            <th>Rate (%)</th>
            <th aria-label="Actions">&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {formatFees(displayFees).map((fee, i) => (
            <tr key={fee.name}>
              <td data-testid="fee-name">
                <Select
                  disabled={readonly}
                  value={displayFees[i].feeType}
                  onChange={v => {
                    if (onFeePropertyChange) {
                      onFeePropertyChange(i, 'feeType', v.target.value as ApiFeeType)
                    }
                    if (onChangeClient) {
                      switch (feeCategory) {
                        case 'note':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultNoteFees[i].feeType = v.target
                                .value as ApiFeeType)
                          )
                          break
                        case 'portfolio':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultPortfolioFees[i].feeType = v.target
                                .value as ApiFeeType)
                          )
                          break
                        case 'strategy':
                          onChangeClient(x => {
                            x.defaultFees.defaultStrategyFees[strategyFeeIndex].fees[i].feeType = v
                              .target.value as ApiFeeType
                          })
                      }
                    }
                  }}
                >
                  {availableFeeTypes.map(c => (
                    <option key={c} value={c}>
                      {unpascalFeeType(c)}
                    </option>
                  ))}
                </Select>
              </td>
              <td data-testid="fee-calculated-on">
                <Select
                  disabled={readonly}
                  value={displayFees[i].feeBasis}
                  onChange={v => {
                    if (onFeePropertyChange) {
                      onFeePropertyChange(i, 'feeBasis', v.target.value as ApiFeeBasis)
                    }
                    if (onChangeClient) {
                      switch (feeCategory) {
                        case 'note':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultNoteFees[i].feeBasis = v.target
                                .value as ApiFeeBasis)
                          )
                          break
                        case 'portfolio':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultPortfolioFees[i].feeBasis = v.target
                                .value as ApiFeeBasis)
                          )
                          break
                        case 'strategy':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultStrategyFees[strategyFeeIndex].fees[
                                i
                              ].feeBasis = v.target.value as ApiFeeBasis)
                          )
                      }
                    }
                  }}
                >
                  {availableFeeBases.map(c => (
                    <option key={c} value={c}>
                      {unpascal(c)}
                    </option>
                  ))}
                </Select>
              </td>
              <td data-testid="fee-frequency">
                <Select
                  disabled={readonly}
                  value={displayFees[i].feeDeductionFrequency}
                  onChange={v => {
                    if (onFeePropertyChange) {
                      onFeePropertyChange(
                        i,
                        'feeDeductionFrequency',
                        v.target.value as ApiFeeDeductionFrequency
                      )
                    }
                    if (onChangeClient) {
                      switch (feeCategory) {
                        case 'note':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultNoteFees[i].feeDeductionFrequency = v.target
                                .value as ApiFeeDeductionFrequency)
                          )
                          break
                        case 'portfolio':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultPortfolioFees[i].feeDeductionFrequency = v
                                .target.value as ApiFeeDeductionFrequency)
                          )
                          break
                        case 'strategy':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultStrategyFees[strategyFeeIndex].fees[
                                i
                              ].feeDeductionFrequency = v.target.value as ApiFeeDeductionFrequency)
                          )
                      }
                    }
                  }}
                >
                  {availableFeeFreqs.map(c => (
                    <option key={c} value={c}>
                      {unpascal(c)}
                    </option>
                  ))}
                </Select>
              </td>
              <td data-testid="fee-vat">
                <Select
                  disabled={readonly}
                  value={displayFees[i].isVATApplicable ? 'Yes' : 'No'}
                  onChange={v => {
                    if (onFeePropertyChange) {
                      onFeePropertyChange(i, 'isVATApplicable', v.target.value === 'Yes')
                    }
                    if (onChangeClient) {
                      switch (feeCategory) {
                        case 'note':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultNoteFees[i].isVATApplicable =
                                v.target.value === 'Yes')
                          )
                          break
                        case 'portfolio':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultPortfolioFees[i].isVATApplicable =
                                v.target.value === 'Yes')
                          )
                          break
                        case 'strategy':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultStrategyFees[strategyFeeIndex].fees[
                                i
                              ].isVATApplicable = v.target.value === 'Yes')
                          )
                      }
                    }
                  }}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </Select>
              </td>
              <td data-testid="fee-counterparty">
                <Select
                  disabled={readonly}
                  value={displayFees[i].counterPartyId}
                  onChange={v => {
                    if (onFeePropertyChange) {
                      onFeePropertyChange(i, 'counterPartyId', v.target.value)
                    }
                    if (onChangeClient) {
                      switch (feeCategory) {
                        case 'note':
                          onChangeClient(
                            x => (x.defaultFees.defaultNoteFees[i].counterPartyId = v.target.value)
                          )
                          break
                        case 'portfolio':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultPortfolioFees[i].counterPartyId =
                                v.target.value)
                          )
                          break
                        case 'strategy':
                          onChangeClient(x => {
                            x.defaultFees.defaultStrategyFees[strategyFeeIndex].fees[
                              i
                            ].counterPartyId = v.target.value
                          })
                      }
                    }
                  }}
                >
                  {allCptyIds.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </td>
              <td data-testid="fee-amount">
                <TextInput
                  fontSize="11px"
                  padding="8px 4px"
                  readOnly={readonly}
                  value={displayFees[i].feeRate * 100}
                  onChange={v => {
                    if (onFeePropertyChange) {
                      onFeePropertyChange(i, 'feeRate', Number(v.target.value) / 100)
                    }
                    if (onChangeClient) {
                      switch (feeCategory) {
                        case 'note':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultNoteFees[i].feeRate =
                                Number(v.target.value) / 100)
                          )
                          break
                        case 'portfolio':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultPortfolioFees[i].feeRate =
                                Number(v.target.value) / 100)
                          )
                          break
                        case 'strategy':
                          onChangeClient(
                            x =>
                              (x.defaultFees.defaultStrategyFees[strategyFeeIndex].fees[i].feeRate =
                                Number(v.target.value) / 100)
                          )
                      }
                    }
                  }}
                />
              </td>
              <td>
                {!readonly && (onRemoveExistingFee || onChangeClient) && (
                  <AddRemoveButton
                    isDisabled={false}
                    operationType="remove"
                    onClick={() => {
                      if (onRemoveExistingFee) {
                        onRemoveExistingFee(i)
                      }
                      if (onChangeClient) {
                        switch (feeCategory) {
                          case 'note':
                            onChangeClient(x => x.defaultFees.defaultNoteFees.splice(i, 1))
                            break
                          case 'portfolio':
                            onChangeClient(x => x.defaultFees.defaultPortfolioFees.splice(i, 1))
                            break
                          case 'strategy':
                            onChangeClient(x =>
                              x.defaultFees.defaultStrategyFees[strategyFeeIndex].fees.splice(i, 1)
                            )
                        }
                      }
                    }}
                  >
                    Remove
                  </AddRemoveButton>
                )}
              </td>
            </tr>
          ))}
          {onChangeNewFee && (
            <tr>
              <td>
                <Select
                  disabled={readonly}
                  value={newFee.feeType}
                  onChange={v => onChangeNewFee(x => (x.feeType = v.target.value as ApiFeeType))}
                >
                  <option>Choose</option>
                  {availableFeeTypes.map(c => (
                    <option key={c} value={c}>
                      {unpascalFeeType(c)}
                    </option>
                  ))}
                </Select>
              </td>
              <td>
                <Select
                  disabled={readonly}
                  value={newFee.feeBasis}
                  onChange={v => onChangeNewFee(x => (x.feeBasis = v.target.value as ApiFeeBasis))}
                >
                  <option>Choose</option>
                  {availableFeeBases.map(c => (
                    <option key={c} value={c}>
                      {unpascal(c)}
                    </option>
                  ))}
                </Select>
              </td>
              <td>
                <Select
                  disabled={readonly}
                  value={newFee.feeDeductionFrequency}
                  onChange={v =>
                    onChangeNewFee(
                      x => (x.feeDeductionFrequency = v.target.value as ApiFeeDeductionFrequency)
                    )
                  }
                >
                  <option>Choose</option>
                  {availableFeeFreqs.map(c => (
                    <option key={c} value={c}>
                      {unpascal(c)}
                    </option>
                  ))}
                </Select>
              </td>
              <td>
                <Select
                  disabled={readonly}
                  value={
                    // eslint-disable-next-line no-nested-ternary
                    newFee.isVATApplicable === null ? 'None' : newFee.isVATApplicable ? 'Yes' : 'No'
                  }
                  onChange={v =>
                    onChangeNewFee(x => (x.isVATApplicable = v.target.value === 'Yes'))
                  }
                >
                  <option value="None">Choose</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </Select>
              </td>
              <td>
                <Select
                  disabled={readonly}
                  value={newFee.counterPartyId}
                  onChange={v => onChangeNewFee(x => (x.counterPartyId = v.target.value))}
                >
                  <option>Choose</option>
                  {allCptyIds.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </td>
              <td>
                <TextInput
                  fontSize="11px"
                  padding="8px 4px"
                  readOnly={readonly}
                  value={newFee.feeRate * 100}
                  onChange={v => onChangeNewFee(x => (x.feeRate = Number(v.target.value) / 100))}
                />
              </td>
              <td>
                {!readonly && (
                  <AddRemoveButton
                    isDisabled={
                      newFee.feeType === ('' as ApiFeeType) ||
                      newFee.feeBasis === ('' as ApiFeeBasis) ||
                      newFee.feeDeductionFrequency === ('' as ApiFeeDeductionFrequency) ||
                      newFee.feeRate === null ||
                      newFee.isVATApplicable === null ||
                      newFee.counterPartyId === ''
                    }
                    operationType="add"
                    onClick={() => {
                      newFee.feeDayCount = 'Act_365'
                      newFee.feeId = getGUID()
                      if (onChangeClient) {
                        switch (feeCategory) {
                          case 'note':
                            onChangeClient(x => x.defaultFees.defaultNoteFees.push(newFee))
                            break
                          case 'portfolio':
                            onChangeClient(x => x.defaultFees.defaultPortfolioFees.push(newFee))
                            break
                          case 'strategy':
                            onChangeClient(x =>
                              x.defaultFees.defaultStrategyFees[strategyFeeIndex].fees.push(newFee)
                            )
                        }
                        onChangeNewFee(x => {
                          x.feeType = '' as ApiFeeType
                          x.feeBasis = '' as ApiFeeBasis
                          x.feeDeductionFrequency = '' as ApiFeeDeductionFrequency
                          x.isVATApplicable = null
                          x.feeRate = null
                        })
                      }
                    }}
                  >
                    <Space isflex alignItems="center">
                      <Icon name="add" size="small" />
                      <Space ml={2}>Add</Space>
                    </Space>
                  </AddRemoveButton>
                )}
              </td>
            </tr>
          )}
          {onAddNewFee && newFeeAllowed && (
            <tr>
              <td>
                <Select
                  disabled={readonly}
                  value={emptyNewFee.feeType}
                  onChange={v => onNewFeePropertyUpdate('feeType', v.target.value as ApiFeeType)}
                >
                  <option value="">Choose</option>
                  {availableFeeTypes.map(c => (
                    <option key={c} value={c}>
                      {unpascalFeeType(c)}
                    </option>
                  ))}
                </Select>
              </td>
              <td>
                <Select
                  disabled={readonly}
                  value={emptyNewFee.feeBasis}
                  onChange={v => onNewFeePropertyUpdate('feeBasis', v.target.value as ApiFeeBasis)}
                >
                  <option value="">Choose</option>
                  {availableFeeBases.map(c => (
                    <option key={c} value={c}>
                      {unpascal(c)}
                    </option>
                  ))}
                </Select>
              </td>
              <td>
                <Select
                  disabled={readonly}
                  value={emptyNewFee.feeDeductionFrequency}
                  onChange={v =>
                    onNewFeePropertyUpdate(
                      'feeDeductionFrequency',
                      v.target.value as ApiFeeDeductionFrequency
                    )
                  }
                >
                  <option value="">Choose</option>
                  {availableFeeFreqs.map(c => (
                    <option key={c} value={c}>
                      {unpascal(c)}
                    </option>
                  ))}
                </Select>
              </td>
              <td>
                <Select
                  disabled={readonly}
                  value={
                    // eslint-disable-next-line no-nested-ternary
                    emptyNewFee.isVATApplicable === null
                      ? null
                      : emptyNewFee.isVATApplicable
                      ? 'Yes'
                      : 'No'
                  }
                  onChange={v =>
                    onNewFeePropertyUpdate('isVATApplicable', v.target.value === 'Yes')
                  }
                >
                  <option value={null}>Choose</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </Select>
              </td>
              <td>
                <Select
                  disabled={readonly}
                  value={emptyNewFee.counterPartyId}
                  onChange={v => onNewFeePropertyUpdate('counterPartyId', v.target.value)}
                >
                  <option value="">Choose</option>
                  {allCptyIds.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </td>
              <td>
                <TextInput
                  fontSize="11px"
                  padding="8px 4px"
                  readOnly={readonly}
                  value={emptyNewFee.feeRate * 100}
                  onChange={v => onNewFeePropertyUpdate('feeRate', Number(v.target.value) / 100)}
                />
              </td>
              <td>
                {!readonly && (
                  <AddRemoveButton
                    isDisabled={
                      emptyNewFee.feeType === ('' as ApiFeeType) ||
                      emptyNewFee.feeBasis === ('' as ApiFeeBasis) ||
                      emptyNewFee.feeDeductionFrequency === ('' as ApiFeeDeductionFrequency) ||
                      emptyNewFee.feeRate === null ||
                      emptyNewFee.isVATApplicable === null ||
                      emptyNewFee.counterPartyId === ''
                    }
                    operationType="add"
                    onClick={() => {
                      onAddValidNewFee()
                    }}
                  >
                    <Space isflex alignItems="center" justifyContent="center">
                      <Icon name="add" size="small" />
                      <Space ml={2}>Add</Space>
                    </Space>
                  </AddRemoveButton>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </StyledTable>
    </StyledFeesBox>
  )
}
